
import PropertyList from './PropertyList.js';
import path from 'path-browserify';
import Version from './VersionVM.mjs';

/**
 */
class ResourceVM
{
	constructor(FS, href)
	{
		this.FS = FS;
		/**
		 * Resource path (not including any host details)
		 * @type {Observable<string>}
		 */
		if (ko.isObservable(href))
			this.href = href;
		else
			this.href = ko.observable(href);

		this.href.subscribe((nv) => {
			this.name(path.basename(nv));
			if (nv)
				this.populate();
		});

		/**
		 */
		this.name = ko.observable('');

		/**
		 * Properties
		 */
		this.property_list = new PropertyList();
		
		/**
		 * QNamed Properties
		 */
		this.qprops = ko.observable({});

		/**
		 * Children in the resource collection (only applicable if the resource is a collection resource)
		 * @type {ResourceEntry[]}
		 */
		this.children = ko.observableArray([]);

		/**
		 * Primary resource type - can be 'collection' or 'regular'
		 * @type {string}
		 */
		this.resourcetype = ko.observable(null);

		/**
		 * All resource types
		 * @type {Array<Object {name, nsuri}>}
		 */
		this.resourcetypes = ko.observableArray([]);

		/**
		 * Current user privileges
		 */
		this.current_user_privileges = ko.observableArray([]);
		
		/**
		 * Will be true for collection resources
		 */
		this.collection = ko.observable(false);
		
		/**
		 * Instances of Version
		 */
		this.versions = ko.observableArray([]);

		/**
		 * Versioning status. Should be NA, CO or CI
		 */
		this.versioning_status = ko.observable('');

		/**
		 * Checked-out href
		 */
		this.checked_out = ko.observable(null);

		/**
		 * Checked-in href
		 */
		this.checked_in = ko.observable(null);



		/**
		 * This property will be set if an error occured while loading data
		 * @type {Object}
		 */
		this.error = ko.observable();
		
		/**
		 * Is Selected
		 */
		this.selected = ko.observable(false);

		/**
		 * Selected version
		 */
		this.selected_version = ko.observable(null);

		/**
		 * Current version - latest checked-in or NA version
		 */
		this.current_version = ko.observable(null);

		/**
		 * Draft version
		 */
		this.draft_version = ko.observable(null);

		/**
		 * Current version label
		 */
		this.version_label = ko.observable(null);

		/**
		 * Current version nr
		 */
		this.current_version_nr = ko.observable(null);
	
		/**
		 * Draft version nr
		 */
		this.draft_version_nr = ko.observable();

		/**
		 * Draft version href
		 */
		this.draft_version_href = ko.observable();


		/**
		 * Lock token to include in write requests
		 */
		this.locktoken = ko.observable(null);

		/**
		 * Locks
		 */
		this.locks = ko.observableArray();

		/**
		 * Owner
		 */
		this.owner = ko.observable();

		this.owner_displayname = ko.computed(() => {
			if (!this.owner())
				return '';
			if (this.owner().name)
				return this.owner().name;
			return '';
		});

		/**
		 */
		this.has_locks = ko.observable(false);
		this.has_exclusive_locks = ko.observable(false);
		this.has_shared_locks = ko.observable(false);
		this.has_indirect_locks = ko.observable(false);
		this.has_direct_locks = ko.observable(false);

		this.can_read = ko.observable(false);
		this.can_write = ko.observable(false);
		this.can_bind = ko.observable(false);
		this.can_unbind = ko.observable(false);
		this.can_checkout = ko.observable(false);
		this.can_checkin = ko.observable(false);
		this.can_lock = ko.observable(false);
		this.can_unlock = ko.observable(false);
	
	}


	get_version(version_nr) { return this.versions().find((x) => x.version() == version_nr) }
	get_version_by_label(label) { return this.versions().find((x) => x.labels().includes(label)) }

	get(nsuri, name) { return this.property_list.get(nsuri, name); }
	set(nsuri, name, value) { return this.property_list.set(nsuri, name, value); }

	/**
	 * Fetch basic resource info to see what we're dealing with
	 */
	async fetch()
	{
		let fetch_properties = [
			{nsuri: 'DAV:', name: 'lockdiscovery'},
			{nsuri: 'DAV:', name: 'checked-out'},
			{nsuri: 'DAV:', name: 'checked-in'},
			{nsuri: 'DAV:', name: 'current-user-privilege-set'},
			{nsuri: 'DAV:', name: 'owner'},
			{nsuri: 'DAV:', name: 'resourcetype'},
			{nsuri: 'PS:', name: 'draft'}
		];
		if (!this.href())
			return;
		let list_result = await this.FS.list(this.href(), 1, fetch_properties);
		if (!list_result)
			throw new Error(`Error while listing "${this.href()}"`);

		if (typeof(list_result) === 'object' && list_result.error)
		{
			this.error = list_result.error;
			throw {code: this.error.code, message: `Error while listing "${this.href()}": ${this.error.message}`};
		}

		let result = list_result.find((r) => path.relative(r.full_path, this.href()) == '');
		if (!result)
			throw new Error('Resource not found in result list');
		
		let proplist = new PropertyList(result.properties);
		this.update_basic_info(proplist);


		let children = [];
		for (let result of list_result)
		{
			if (path.relative(result.full_path, this.href()) != '')
			{
				let child = this.children().find((child) => { return child.href() == result.full_path; });
				if (!child)
					child = new ResourceVM(this.FS, result.full_path);
				proplist = new PropertyList(result.properties);
				child.update_basic_info(proplist);
				children.push(child);
			}
		}

		this.children(children);

		if (this.versioning_status() != 'NA')
			await this.fetch_versions();
		else
		{
			const version = new Version(this.FS, this, this.href());
			version.set_properties(proplist);
			await version.fetch([
				'{DAV:}resourcetype',
				'{DAV:}creator-displayname',
				'{DAV:}getlastmodified',
				'{DAV:}comment',
				'{DAV:}getcontentlength'
			]);

			this.versions([version]);
			this.current_version(version);
		}
	}
	
	async fetch_versions(props=[])
	{
		let get_props = props;
		if (!props || !props.length)
		{
			get_props = [
				'{DAV:}resourcetype',
				'{DAV:}creator-displayname',
				'{DAV:}getlastmodified',
				'{DAV:}comment',
				'{DAV:}getcontentlength'
			];
		}
		const versions = this.versions();
		const report = await this.report('DAV:', 'version-tree', { get: get_props });
		let max_ci_version = 0;
		for (let v of report || [])
		{
			let version = this.versions().find((x) => x.version() === v.version);
			if (!version)
			{
				version = new Version(this.FS, this, v.href);
				version.version(v.version); // Version number
				versions.push(version);
			}
			version.version_label(v.labels[0]); 
			version.labels(v.labels); 
			version.set_properties(v.properties.values);
			console.log('VERSION=',version);
			
			if (this.draft_version_nr() && version.version() == this.draft_version_nr())
			{
				version.status('CO');
				this.draft_version(version);
			}
			else
				version.status('CI');

			if (this.checked_out())
			{
				if (path.relative(this.checked_out().href, version.href()) == '')
				{
					this.current_version(version);
				}
			}
			else if (this.checked_in())
			{
				if (path.relative(this.checked_in().href, version.href()) == '')
				{
					this.current_version(version);
				}
			}
			else
			{
				if (version.version() > max_ci_version)
					max_ci_version = version.version();
			}

		}
		if (!this.current_version())
			this.current_version(versions.find((x) => x.version() == max_ci_version));

		versions.sort((x, y) => y.version() - x.version());
		this.versions(versions);

		console.log('Versioning status=', this.versioning_status());
		console.log('Current version=', this.current_version());
		console.log('Draft version=', this.draft_version());
		console.log('Versions=', this.versions());
	}

	/**
	 * Sets standard (non-version affected) fields on this class based on the provided PropertyList
	 */
	update_basic_info(proplist)
	{
		// Resource type
		const resourcetype = proplist.get('DAV:', 'resourcetype');
		if (resourcetype)
		{
			if (Array.isArray(resourcetype))
			{
				this.collection(false);
				for (let rs of resourcetype)
				{
					if (rs.nsuri == 'DAV:' && rs.name == 'collection')
						this.collection(true);
				}
				this.resourcetypes(resourcetype);
			}
			else
			{
				if (resourcetype == 'collection')
					this.collection(true);
				else
					this.collection(false);
				this.resourcetypes([{nsuri: 'DAV:', name: resourcetype}]);
			}
		}

		// Update lock info
		// See if we have a locktoken in localStorage for this resource
		let stored_locktoken = this.find_localstorage_locktoken(this.href());
		if (stored_locktoken)
			this.locktoken(stored_locktoken);

		// Locks
		let locks = proplist.get('DAV:', 'lockdiscovery') || [];
		if (locks.length)
		{
			this.has_locks(true);
			for (let lock of locks)
			{
				// Deal with cases where lockroot is for example '/dsds/dsds' but href() is '/dsds/dsds/'
				let lockroot = lock.lockroot;
				if (this.href().endsWith('/') && !lock.lockroot.endsWith())
					lockroot += '/';

				if (lockroot == this.href())
				{
					this.has_direct_locks(true);
					lock.direct = true;
				}
				else
				{
					this.has_indirect_locks(true);
					lock.direct = false;
				}
				
				if (lock.lockscope == 'exclusive')
					this.has_exclusive_locks(true);
				else if (lock.lockscope == 'shared')
					this.has_shared_locks(true);
			}
			this.can_lock(false);
		}
		else
		{
			this.has_locks(false);
			this.has_exclusive_locks(false);
			this.has_shared_locks(false);
			this.has_indirect_locks(false);
			this.has_direct_locks(false);
			this.can_lock(true);
		}

		// If we have a stored locktoken, but the resource is not directly lock, remove our locktoken
		if (this.locktoken() && !this.has_direct_locks())
		{
			this.remove_localstorage_locktoken(this.locktoken());
			this.locktoken(null);
		}

		this.locks(locks);

		const owner = proplist.get('DAV:', 'owner');
		this.owner(owner);

		const draft = proplist.get('PS:', 'draft');
		if (draft)
		{
			this.draft_version_nr(draft.version);
			this.draft_version_href(draft.href);
		}

		// Privileges
		let current_user_privileges = proplist.get('DAV:', 'current-user-privilege-set') || [];
		this.current_user_privileges(current_user_privileges);

		if (current_user_privileges.indexOf('read') > -1)
			this.can_read(true);

		if (current_user_privileges.indexOf('write') > -1)
			this.can_write(true);

		if (current_user_privileges.indexOf('bind') > -1)
			this.can_bind(true);

		if (current_user_privileges.indexOf('unbind') > -1)
			this.can_unbind(true);
		
		let ci = proplist.get('DAV:', 'checked-in');
		this.checked_in(ci);
		if (ci)
		{
			this.versioning_status('CI');
			this.can_checkout(true);
		}

		let co = proplist.get('DAV:', 'checked-out');
		this.checked_out(co);
		if (co)
		{
			this.versioning_status('CO');
			this.can_checkin(true);
		}

		if (!ci && !co)
		{
			this.versioning_status('NA');
			this.can_checkout(true);
		}

	}

	set_properties(properties)
	{
		this.property_list.set_properties(properties);
		this.update_basic_info(this.property_list);
	}

	async save_property(nsuri, name, value)
	{
		return this.save_properties([{nsuri: nsuri, name: name, value: value}]);
	}
	
	async save_properties(properties, remove_properties=[])
	{
		let options = {};
		if (this.locktoken())
			options['locktoken'] = this.locktoken();
		return this.FS.update(this.href(), properties, remove_properties, options);
	}

	property_value(nsuri, name)
	{
		return this.property_list.get(nsuri, name);
	}

	async populate(properties=[])
	{
		if (!this.href())
			return;
		let list_result = await this.FS.list(this.href(), 1, properties, this.version_label());
		if (!list_result)
			throw new Error(`Error while listing "${this.href()}"`);

		if (typeof(list_result) === 'object' && list_result.error)
		{
			this.error = list_result.error;
			throw {code: this.error.code, message: `Error while listing "${this.href()}": ${this.error.message}`};
		}

		let children = [];
		for (let result of list_result)
		{
			if (path.relative(result.full_path, this.href()) == '')
			{
				this.set_properties(result.properties);
			}
			else
			{
				let child = this.children().find((child) => { return child.href() == result.full_path; });
				if (!child)
					child = new ResourceVM(this.FS, result.full_path);
				child.set_properties(result.properties);
				child.set('ui:', 'name', path.basename(result.full_path));
				children.push(child);
			}
		}

		this.children(children);
	}

	populate_qprops(properties)
	{
		let qprops = {};
		for (let prop of properties)
		{
			let qname = `{${prop.nsuri}}${prop.name}`;
			qprops[qname] = prop.value;
		}
		this.qprops(qprops);
	}

	update_lockinfo()
	{
	}

	async report(report_nsuri, report_name, options)
	{
		return this.FS.report(this.href(), report_nsuri, report_name, options);
	}

	async checkout()
	{
		return this.FS.checkout(this.href());
	}

	async checkin()
	{
		return this.FS.checkin(this.href());
	}

	async lock(type, scope, depth, lifetime)
	{
		let lockinfo = {
			type: type,
			scope: scope,
			depth: depth,
			lifetime: lifetime
		};
		let lockresult = await this.FS.lock(this.href(), lockinfo);

		if (lockresult.locktoken)
		{
			this.add_localstorage_locktoken(this.href(), lockresult.locktoken);
			this.locktoken(lockresult.locktoken);
		}

		return lockresult;
	}

	async unlock(locktoken)
	{
		console.log('UNLOCK', locktoken);
		let result = await this.FS.unlock(this.href(), locktoken);
		if (locktoken === this.locktoken())
		{
			this.locktoken(null);
			this.remove_localstorage_locktoken(locktoken);
		}
		return locktoken;
	}

	add_localstorage_locktoken(url, locktoken)
	{
		let locktokens;
		try {
			locktokens = JSON.parse(localStorage.getItem('cms.locktokens') || '[]');
		} catch (err) {
			locktokens = [];
		}
		
		locktokens.push({url: url, locktoken: locktoken});
		console.log('SAVING LOCKTOKENS', locktokens);
		localStorage.setItem('cms.locktokens', JSON.stringify(locktokens));
	}

	find_localstorage_locktoken(url)
	{
		let locktokens;
		try {
			locktokens = JSON.parse(localStorage.getItem('cms.locktokens') || '[]');
		} catch (err) {
			locktokens = [];
		}
		
		let lock = locktokens.find((x) => x.url == url);
		if (!lock)
			return null;
		return lock.locktoken || null;
	}

	remove_localstorage_locktoken(locktoken)
	{
		try {
			let locktokens = JSON.parse(localStorage.getItem('cms.locktokens') || '[]');
			locktokens = locktokens.filter((x) => x.locktoken != locktoken);
			localStorage.setItem('cms.locktokens', JSON.stringify(locktokens));
		} catch (err) {
			localStorage.setItem('cms.locktokens', JSON.stringify([]));
		}
	}
}

export default ResourceVM;

